<script setup lang="ts">
interface Feature {
  id: string
  name: string
  icon: string
  label?: string
  theme: string
}

interface Props {
  feature?: Feature
  // Alternative individual props for web components
  featureId?: string
  featureName?: string
  featureIcon?: string
  featureTheme?: string
  featureLabel?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'light' | 'dark'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  variant: 'light'
})

// Use individual props if feature object is not provided (for web components)
const actualFeature = computed(() => {
  if (props.feature) {
    return props.feature
  }
  return {
    id: props.featureId || '',
    name: props.featureName || '',
    icon: props.featureIcon || '',
    theme: props.featureTheme || 'pill--camera',
    label: props.featureLabel || 'FOOBAR'
  }
})

const sizeClasses = {
  sm: 'px-2 py-1 gap-1',
  md: 'px-3 py-1 gap-2',
  lg: 'px-4 py-2 gap-3'
}

const iconSizeClasses = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8'
}

const textSizeClasses = {
  sm: 'text-xs',
  md: 'text-xs xl:text-sm',
  lg: 'text-sm xl:text-base'
}

// Theme-based styling
const getThemeStyles = (theme: string, variant: 'light' | 'dark') => {
  const styles = {
    borderColor: '',
    background: '',
    textColor: variant === 'light' ? 'text-warm-asphalt' : 'text-white'
  }

  const themeType = theme.replace('pill--', '')

  if (variant === 'light') {
    switch (themeType) {
      case 'camera':
        styles.borderColor = 'border-[#00aada]'
        styles.background = 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(0, 170, 218, 0.3) 100%)'
        break
      case 'limit':
      case 'school':
      case 'traffic':
        styles.borderColor = 'border-[#f4b88c]'
        styles.background = 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(244, 184, 140, 0.3) 100%)'
        break
      case 'danger':
      case 'servicetrack':
        styles.borderColor = 'border-[#ff2115]'
        styles.background = 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(236, 15, 3, 0.3) 100%)'
        break
      case 'animals':
        styles.borderColor = 'border-[#66c489]'
        styles.background = 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(102, 196, 137, 0.3) 100%)'
        break
      case 'speeding':
      case 'currentspeed':
        styles.borderColor = 'border-[#dc9d2f]'
        styles.background = 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(220, 157, 47, 0.3) 100%)'
        break
    }
  } else {
    // Dark variant
    switch (themeType) {
      case 'camera':
        styles.borderColor = 'border-[#00aada]'
        styles.background = 'linear-gradient(270deg, #005066 13.62%, rgba(0, 0, 0, 0.3) 85.1%)'
        break
      case 'limit':
      case 'school':
        styles.borderColor = 'border-[#f4b88c]'
        styles.background = 'linear-gradient(270deg, #5d2d09 13.62%, rgba(0, 0, 0, 0.3) 85.1%)'
        break
      case 'traffic':
        styles.borderColor = 'border-[#f4b88c]'
        styles.background = 'linear-gradient(270deg, #442f22 13.62%, rgba(0, 0, 0, 0.3) 85.1%)'
        break
      case 'danger':
      case 'servicetrack':
        styles.borderColor = 'border-[#ff2115]'
        styles.background = 'linear-gradient(270deg, #660500 13.62%, rgba(0, 0, 0, 0.3) 85.1%)'
        break
      case 'animals':
        styles.borderColor = 'border-[#66c489]'
        styles.background = 'linear-gradient(270deg, #1c4a2d 13.62%, rgba(0, 0, 0, 0.3) 85.1%)'
        break
      case 'speeding':
      case 'currentspeed':
        styles.borderColor = 'border-[#dc9d2f]'
        styles.background = 'linear-gradient(270deg, #573d0f 13.62%, rgba(0, 0, 0, 0.3) 85.1%)'
        break
    }
  }

  return styles
}

const themeStyles = computed(() => getThemeStyles(actualFeature.value.theme, props.variant))

const pillClasses = computed(() => [
  'inline-flex items-center rounded-full font-medium border border-solid whitespace-nowrap',
  sizeClasses[props.size],
  themeStyles.value.borderColor,
  themeStyles.value.textColor
])
</script>

<template>
  <li
    :class="pillClasses"
    :style="{ background: themeStyles.background }"
  >
    <div v-if="actualFeature.label" class="bg-accent rounded-full text-crosswalk px-2 py-1 text-[10px] font-semibold absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-[90%]">
      {{ actualFeature.label }}
    </div>
    <div
      class="flex items-center justify-center"
      :class="iconSizeClasses[size]"
    >
      <img :src="actualFeature.icon" :alt="actualFeature.name" />
    </div>
    <span :class="textSizeClasses[size]">{{ actualFeature.name }}</span>
  </li>
</template>
